import { HIGHLIGHT_CARD_TYPES } from './constants/reporting-dashboard/highlight-card.constants';

/**
 * Assignment Report View wording that differs from the User Report View for the same
 * cardType. Card types not listed here have identical copy in both views and resolve
 * via getDescriptionKey() instead — do not add a card type here unless its assignment-view
 * translation key actually exists and differs from the user-view one.
 */
const ASSIGNMENT_DESCRIPTION_KEYS: Record<string, string> = {
  [HIGHLIGHT_CARD_TYPES.LEAST_MISSED_QUESTION]: 'QUESTION_CORRECT_PERCENTAGE_DESC_ASSIGNMENT',
  [HIGHLIGHT_CARD_TYPES.MOST_MISSED_QUESTION]: 'QUESTION_CORRECT_PERCENTAGE_DESC_ASSIGNMENT',
  [HIGHLIGHT_CARD_TYPES.LEAST_MISSED_ANSWER_POINT]: 'QUESTION_CORRECT_PERCENTAGE_DESC_ASSIGNMENT',
  [HIGHLIGHT_CARD_TYPES.MOST_MISSED_ANSWER_POINT]: 'QUESTION_CORRECT_PERCENTAGE_DESC_ASSIGNMENT',
  [HIGHLIGHT_CARD_TYPES.MOST_ARTICULATED_MESSAGE]: 'MOST_ARTICULATED_MESSAGE_DESCRIPTION_ASSIGNMENT',
  [HIGHLIGHT_CARD_TYPES.LEAST_ARTICULATED_MESSAGE]: 'LEAST_ARTICULATED_MESSAGE_DESCRIPTION_ASSIGNMENT',
};

/**
 * Utility service for mapping highlight card types to translation keys and other metadata.
 * Centralizes common mapping logic used across multiple highlight card components.
 */
export class HighlightCardMappingUtil {
  /**
   * Maps a highlight card type to its heading translation key
   * @param cardType - The type of highlight card
   * @returns Translation key for the card heading
   */
  static getHeadingKey(cardType: string): string {
    switch (cardType) {
      // User and shared card types
      case HIGHLIGHT_CARD_TYPES.LEAST_MISSED_QUESTION:
        return 'LEAST_MISSED_QUESTION';
      case HIGHLIGHT_CARD_TYPES.MOST_MISSED_QUESTION:
        return 'MOST_MISSED_QUESTION';
      case HIGHLIGHT_CARD_TYPES.HIGHEST_SCORING_METHODOLOGY:
        return 'HIGHEST_SCORING_METHODOLOGY_SECTION';
      case HIGHLIGHT_CARD_TYPES.LOWEST_SCORING_METHODOLOGY:
        return 'LOWEST_SCORING_METHODOLOGY_SECTION';
      case HIGHLIGHT_CARD_TYPES.LEAST_ARTICULATED_MESSAGE:
        return 'LEAST_ARTICULATED_MESSAGE';
      case HIGHLIGHT_CARD_TYPES.MOST_ARTICULATED_MESSAGE:
        return 'MOST_ARTICULATED_MESSAGE';
      case HIGHLIGHT_CARD_TYPES.USER_ENGAGEMENT:
        return 'ENGAGEMENT';
      // Practice-specific card types
      case HIGHLIGHT_CARD_TYPES.LEAST_MISSED_ANSWER_POINT:
        return 'LEAST_MISSED_ANSWER_POINT';
      case HIGHLIGHT_CARD_TYPES.MOST_MISSED_ANSWER_POINT:
        return 'MOST_MISSED_ANSWER_POINT';
      default:
        return cardType;
    }
  }

  /**
   * Maps a highlight card type to its description translation key
   * @param cardType - The type of highlight card
   * @param isNegative - Optional flag for USER_ENGAGEMENT to differentiate descriptions
   * @returns Translation key for the card description
   */
  static getDescriptionKey(cardType: string, isNegative: boolean = false): string {
    // Special handling for USER_ENGAGEMENT card type
    if (cardType === HIGHLIGHT_CARD_TYPES.USER_ENGAGEMENT) {
      return isNegative ? 'USER_ENGAGEMENT_FEWER_SESSIONS' : 'USER_ENGAGEMENT_MORE_SESSIONS';
    }

    switch (cardType) {
      // Question and answer point types
      case HIGHLIGHT_CARD_TYPES.MOST_MISSED_QUESTION:
      case HIGHLIGHT_CARD_TYPES.LEAST_MISSED_QUESTION:
      case HIGHLIGHT_CARD_TYPES.LEAST_MISSED_ANSWER_POINT:
      case HIGHLIGHT_CARD_TYPES.MOST_MISSED_ANSWER_POINT:
        return 'QUESTION_CORRECT_PERCENTAGE_DESC';
      // Methodology types
      case HIGHLIGHT_CARD_TYPES.HIGHEST_SCORING_METHODOLOGY:
      case HIGHLIGHT_CARD_TYPES.LOWEST_SCORING_METHODOLOGY:
        return 'METHODOLOGY_AVERAGE_POINTS_DESC';
      // Message articulation types
      case HIGHLIGHT_CARD_TYPES.LEAST_ARTICULATED_MESSAGE:
        return 'LEAST_ARTICULATED_MESSAGE_DESCRIPTION';
      case HIGHLIGHT_CARD_TYPES.MOST_ARTICULATED_MESSAGE:
        return 'MOST_ARTICULATED_MESSAGE_DESCRIPTION';
      default:
        return '';
    }
  }

  /**
   * Same as getDescriptionKey(), but for the Assignment Report View: card types with
   * per-view wording resolve to their _ASSIGNMENT-suffixed key; everything else
   * (e.g. USER_ENGAGEMENT, HIGHEST/LOWEST_SCORING_METHODOLOGY) falls through to
   * getDescriptionKey() unchanged, since their copy doesn't differ between views.
   * @param cardType - The type of highlight card
   * @param isNegative - Optional flag for USER_ENGAGEMENT to differentiate descriptions
   * @returns Translation key for the card description, assignment-view variant where one exists
   */
  static getAssignmentDescriptionKey(cardType: string, isNegative: boolean = false): string {
    return ASSIGNMENT_DESCRIPTION_KEYS[cardType] ?? HighlightCardMappingUtil.getDescriptionKey(cardType, isNegative);
  }
}
